import type { ReactNode } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
    ChevronLeft,
    LayoutDashboard,
    LogIn,
    LogOut,
    Menu,
    X
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { initialsOf } from '@/lib/format'
import Logo from '@/components/generals/logo'
import { Button, ButtonLink } from '@/components/generals/button'
import { useAuth } from '@/components/generals/auth-provider'
import { navLinks } from '@/constants/site'

const HOME_SECTIONS = ['hero', 'courses', 'how-it-works', 'features', 'desktop-app', 'dashboard', 'community', 'get-started']

const SECTION_TO_NAV: Record<string, string> = {
    hero: 'home',
    courses: 'courses',
    'how-it-works': 'how-it-works',
    features: 'features',
    community: 'community'
}

const useActiveSection = (enabled: boolean): string | null => {
    const [active, setActive] = useState<string | null>(null)

    useEffect(() => {
        if (!enabled) {
            setActive(null)
            return
        }

        const elements = HOME_SECTIONS
            .map((id) => document.getElementById(id))
            .filter((element): element is HTMLElement => Boolean(element))

        if (!elements.length) return

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) setActive(entry.target.id)
            })
        }, { rootMargin: '-45% 0px -50% 0px' })

        elements.forEach((element) => observer.observe(element))

        return () => observer.disconnect()
    }, [enabled])

    return active
}

const Header = (): ReactNode => {
    const router = useRouter()
    const { status, user, signOut } = useAuth()
    const [scrolled, setScrolled] = useState(false)
    const [open, setOpen] = useState(false)

    const isHome = router.pathname === '/'
    const activeSection = useActiveSection(isHome)

    const activeMatch = isHome
        ? (activeSection ? SECTION_TO_NAV[activeSection] ?? null : 'home')
        : router.pathname.startsWith('/courses') ? 'courses' : null

    useEffect(() => {
        const onScroll = (): void => setScrolled(window.scrollY > 12)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = (): void => setOpen(false)

        router.events.on('routeChangeStart', close)
        router.events.on('hashChangeStart', close)

        return () => {
            router.events.off('routeChangeStart', close)
            router.events.off('hashChangeStart', close)
        }
    }, [router.events])

    useEffect(() => {
        if (!open) return

        const previous = document.body.style.overflow
        const onKeyDown = (event: KeyboardEvent): void => {
            if (event.key === 'Escape') setOpen(false)
        }

        document.body.style.overflow = 'hidden'
        window.addEventListener('keydown', onKeyDown)

        return () => {
            document.body.style.overflow = previous
            window.removeEventListener('keydown', onKeyDown)
        }
    }, [open])

    const handleSignOut = async (): Promise<void> => {
        setOpen(false)
        await signOut()
        await router.push('/')
    }

    return (
        <header className='fixed inset-x-0 top-0 z-50 px-3 pt-3 sm:px-4'>
            <div
                className={cn(
                    'relative mx-auto flex h-16 max-w-6xl items-center justify-between gap-3 rounded-2xl border px-3 transition-[background-color,box-shadow,border-color] duration-300 sm:px-4',
                    scrolled || open
                        ? 'border-white/80 bg-white/85 shadow-soft backdrop-blur-xl'
                        : 'border-white/40 bg-white/45 backdrop-blur-md'
                )}
            >
                <Logo />

                <nav aria-label="منوی اصلی" className='hidden items-center gap-1 lg:flex'>
                    {navLinks.map((link) => {
                        const active = activeMatch === link.match

                        return (
                            <Link
                                key={link.href}
                                href={link.href}
                                aria-current={active ? 'page' : undefined}
                                className={cn(
                                    'relative isolate rounded-xl px-3.5 py-2 text-sm font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
                                    active ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
                                )}
                            >
                                {active && (
                                    <motion.span
                                        layoutId='nav-active-pill'
                                        className='absolute inset-0 -z-10 rounded-xl bg-primary-soft'
                                        transition={{ type: 'spring', stiffness: 380, damping: 32 }}
                                    />
                                )}
                                {link.label}
                            </Link>
                        )
                    })}
                </nav>

                <div className='flex items-center gap-2'>
                    {status === 'loading' && (
                        <div className='hidden h-9 w-36 animate-pulse rounded-xl bg-white/70 sm:block' aria-hidden='true' />
                    )}

                    {status === 'guest' && (
                        <>
                            <ButtonLink href='/login' variant='ghost' size='sm' className='hidden sm:inline-flex'>
                                ورود
                            </ButtonLink>
                            <ButtonLink href='/signup' size='sm'>
                                ثبت‌نام
                            </ButtonLink>
                        </>
                    )}

                    {status === 'authenticated' && user && (
                        <Link
                            href='/dashboard'
                            className='inline-flex h-10 items-center gap-2 rounded-xl bg-white py-1 ps-1 pe-3 text-sm font-semibold shadow-soft ring-1 ring-border transition hover:text-primary hover:ring-primary/30'
                        >
                            <span className='grid size-8 place-items-center rounded-lg bg-primary text-xs font-bold text-white'>
                                {initialsOf(user.name)}
                            </span>
                            <span className='hidden sm:inline'>داشبورد</span>
                            <LayoutDashboard className='size-4 sm:hidden' aria-hidden='true' />
                        </Link>
                    )}

                    <button
                        type='button'
                        onClick={() => setOpen((value) => !value)}
                        aria-expanded={open}
                        aria-controls='mobile-menu'
                        aria-label={open ? "بستن منو" : "باز کردن منو"}
                        className='grid size-10 place-items-center rounded-xl text-foreground transition hover:bg-white/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 lg:hidden'
                    >
                        <AnimatePresence mode='wait' initial={false}>
                            <motion.span
                                key={open ? 'close' : 'open'}
                                initial={{ rotate: -90, opacity: 0 }}
                                animate={{ rotate: 0, opacity: 1 }}
                                exit={{ rotate: 90, opacity: 0 }}
                                transition={{ duration: 0.18 }}
                                className='grid place-items-center'
                            >
                                {open ? <X className='size-5' /> : <Menu className='size-5' />}
                            </motion.span>
                        </AnimatePresence>
                    </button>
                </div>
            </div>

            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            key='backdrop'
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className='fixed inset-0 -z-10 bg-foreground/15 backdrop-blur-[2px] lg:hidden'
                            onClick={() => setOpen(false)}
                            aria-hidden='true'
                        />

                        <motion.div
                            key='panel'
                            id='mobile-menu'
                            initial={{ opacity: 0, y: -10, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.98 }}
                            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                            className='mx-auto mt-2 max-w-6xl origin-top rounded-2xl border border-white/80 bg-white/95 p-3 shadow-lifted backdrop-blur-xl lg:hidden'
                        >
                            <nav aria-label="منوی موبایل" className='flex flex-col'>
                                {navLinks.map((link) => (
                                    <Link
                                        key={link.href}
                                        href={link.href}
                                        onClick={() => setOpen(false)}
                                        className={cn(
                                            'flex items-center justify-between rounded-xl px-4 py-3 text-base font-semibold transition-colors hover:bg-primary-soft',
                                            activeMatch === link.match ? 'bg-primary-soft text-primary' : 'text-foreground'
                                        )}
                                    >
                                        {link.label}
                                        <ChevronLeft className='size-4 text-muted-foreground' aria-hidden='true' />
                                    </Link>
                                ))}
                            </nav>

                            <div className='mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3'>
                                {status === 'authenticated' ? (
                                    <>
                                        <ButtonLink href='/dashboard' variant='primary'>
                                            <LayoutDashboard />
                                            داشبورد
                                        </ButtonLink>
                                        <Button variant='secondary' onClick={handleSignOut}>
                                            <LogOut />
                                            خروج
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <ButtonLink href='/login' variant='secondary'>
                                            <LogIn />
                                            ورود
                                        </ButtonLink>
                                        <ButtonLink href='/signup' variant='primary'>
                                            ثبت‌نام رایگان
                                        </ButtonLink>
                                    </>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </header>
    )
}

export default Header
